import React, { useState, useEffect } from 'react'
import {
  FileText,
  Plus,
  Eye,
  Send,
  Copy,
  QrCode,
  Filter,
  Search,
  RefreshCw,
  ExternalLink,
  Calendar,
  DollarSign,
  User,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle
} from 'lucide-react'
import { mockApiService } from '../services/mockApiService'
import type { MockInvoice } from '../services/mockApi'
import { InvoiceCreationForm } from './InvoiceCreationForm'

interface InvoiceManagementProps {
  className?: string
}

type ViewMode = 'list' | 'create'

export const InvoiceManagement: React.FC<InvoiceManagementProps> = ({ className = '' }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [invoices, setInvoices] = useState<MockInvoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedInvoice, setSelectedInvoice] = useState<MockInvoice | null>(null)
  const [showQRCode, setShowQRCode] = useState<string | null>(null)

  useEffect(() => {
    loadInvoices()
  }, [statusFilter])

  const loadInvoices = async () => {
    try {
      setError(null)
      const response = await mockApiService.getInvoices({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 20,
        page: 1
      })

      if (response.success) {
        setInvoices(response.data.invoices)
      } else {
        setError(response.error || 'Failed to load invoices')
      }
    } catch (err) {
      setError('An unexpected error occurred')
      console.error('Invoice loading error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleInvoiceCreated = (invoice: MockInvoice) => {
    setInvoices(prev => [invoice, ...prev])
    setViewMode('list')
  }

  const handleSendInvoice = async (invoiceId: string) => {
    try {
      const response = await mockApiService.sendInvoice(invoiceId)
      if (response.success) {
        setInvoices(prev => prev.map(inv => 
          inv.id === invoiceId 
            ? { ...inv, status: 'sent', sentAt: new Date().toISOString() }
            : inv
        ))
      }
    } catch (err) {
      console.error('Send invoice error:', err)
    }
  }

  const handleCopyPaymentLink = async (paymentLink: string) => {
    try {
      await navigator.clipboard.writeText(paymentLink)
      // You could add a toast notification here
    } catch (err) {
      console.error('Copy to clipboard failed:', err)
    }
  }

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-HK', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <FileText className="w-4 h-4" />
      case 'sent': return <Send className="w-4 h-4" />
      case 'paid': return <CheckCircle className="w-4 h-4" />
      case 'overdue': return <AlertCircle className="w-4 h-4" />
      case 'cancelled': return <XCircle className="w-4 h-4" />
      default: return <Clock className="w-4 h-4" />
    }
  }

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'draft': return 'professional-status--info'
      case 'sent': return 'professional-status--warning'
      case 'paid': return 'professional-status--success'
      case 'overdue': return 'professional-status--error'
      case 'cancelled': return 'professional-status--error'
      default: return 'professional-status--info'
    }
  }

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = searchTerm === '' || 
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customerEmail.toLowerCase().includes(searchTerm.toLowerCase())
    
    return matchesSearch
  })

  if (viewMode === 'create') {
    return (
      <InvoiceCreationForm
        onInvoiceCreated={handleInvoiceCreated}
        onCancel={() => setViewMode('list')}
        className={className}
      />
    )
  }

  return (
    <div className={`invoice-management ${className}`}>
      {/* Header */}
      <div className="invoice-management__header">
        <div className="invoice-management__header-content">
          <h2 className="professional-heading-2">Invoice Management</h2>
          <p className="professional-text">
            Create, manage, and track invoices with integrated payment links and QR codes.
          </p>
        </div>
        <div className="invoice-management__header-actions">
          <button
            onClick={loadInvoices}
            className="professional-btn professional-btn--secondary"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setViewMode('create')}
            className="professional-btn professional-btn--primary"
          >
            <Plus className="w-4 h-4" />
            Create Invoice
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="professional-card">
        <div className="professional-card__content">
          <div className="invoice-management__filters">
            <div className="professional-form-group">
              <label className="professional-form-label">
                <Search className="w-4 h-4" />
                Search Invoices
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="professional-form-input"
                placeholder="Search by invoice number, customer name, or email"
              />
            </div>
            
            <div className="professional-form-group">
              <label className="professional-form-label">
                <Filter className="w-4 h-4" />
                Status Filter
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="professional-form-select"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice List */}
      <div className="professional-card">
        <div className="professional-card__header">
          <h3 className="professional-card__title">Invoices</h3>
          <p className="professional-card__subtitle">
            {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''} found
          </p>
        </div>
        
        <div className="professional-card__content">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
              <span className="ml-3 text-gray-600">Loading invoices...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <AlertCircle className="w-8 h-8 text-red-500" />
              <span className="ml-3 text-red-600">{error}</span>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="professional-heading-3 mb-2">No invoices found</h3>
              <p className="professional-text text-gray-600 mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Create your first invoice to get started.'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <button
                  onClick={() => setViewMode('create')}
                  className="professional-btn professional-btn--primary"
                >
                  <Plus className="w-4 h-4" />
                  Create First Invoice
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredInvoices.map((invoice) => (
                <div key={invoice.id} className="invoice-management__invoice-card">
                  <div className="invoice-management__invoice-header">
                    <div className="invoice-management__invoice-info">
                      <div className="invoice-management__invoice-number">
                        {invoice.invoiceNumber}
                      </div>
                      <div className="invoice-management__invoice-meta">
                        <span className="invoice-management__invoice-customer">
                          <User className="w-3 h-3" />
                          {invoice.customerName}
                        </span>
                        <span className="invoice-management__invoice-country">
                          {invoice.customerCountry}
                        </span>
                        <span className="invoice-management__invoice-date">
                          <Calendar className="w-3 h-3" />
                          Due: {new Date(invoice.dueDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="invoice-management__invoice-amount">
                      <div className="invoice-management__amount-value">
                        {formatCurrency(invoice.amount, invoice.currency)}
                      </div>
                      <div className="invoice-management__settlement-rail">
                        {invoice.settlementRail}
                      </div>
                    </div>
                    
                    <div className="invoice-management__invoice-status">
                      <div className={`professional-status ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        <span className="professional-status__text">{invoice.status}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="invoice-management__invoice-description">
                    {invoice.description}
                  </div>
                  
                  <div className="invoice-management__invoice-actions">
                    <button
                      onClick={() => setSelectedInvoice(invoice)}
                      className="professional-btn professional-btn--secondary professional-btn--sm"
                    >
                      <Eye className="w-3 h-3" />
                      View
                    </button>
                    
                    {invoice.status === 'draft' && (
                      <button
                        onClick={() => handleSendInvoice(invoice.id)}
                        className="professional-btn professional-btn--primary professional-btn--sm"
                      >
                        <Send className="w-3 h-3" />
                        Send
                      </button>
                    )}
                    
                    {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                      <>
                        <button
                          onClick={() => handleCopyPaymentLink(invoice.paymentLink)}
                          className="professional-btn professional-btn--secondary professional-btn--sm"
                        >
                          <Copy className="w-3 h-3" />
                          Copy Link
                        </button>
                        
                        <button
                          onClick={() => setShowQRCode(invoice.id)}
                          className="professional-btn professional-btn--secondary professional-btn--sm"
                        >
                          <QrCode className="w-3 h-3" />
                          QR Code
                        </button>
                      </>
                    )}
                    
                    <button
                      onClick={() => window.open(invoice.paymentLink, '_blank')}
                      className="professional-btn professional-btn--secondary professional-btn--sm"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open Link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="professional-modal-overlay" onClick={() => setShowQRCode(null)}>
          <div className="professional-modal" onClick={(e) => e.stopPropagation()}>
            <div className="professional-modal__header">
              <h3 className="professional-modal__title">Payment QR Code</h3>
              <button
                onClick={() => setShowQRCode(null)}
                className="professional-modal__close"
              >
                ×
              </button>
            </div>
            <div className="professional-modal__content">
              {(() => {
                const invoice = invoices.find(inv => inv.id === showQRCode)
                return invoice ? (
                  <div className="text-center">
                    <div className="mb-4">
                      <img
                        src={invoice.qrCode}
                        alt={`QR Code for ${invoice.invoiceNumber}`}
                        className="mx-auto border border-gray-200 rounded-lg"
                        style={{ width: '200px', height: '200px' }}
                      />
                    </div>
                    <p className="professional-text text-gray-600 mb-4">
                      Scan this QR code to access the payment page for invoice {invoice.invoiceNumber}
                    </p>
                    <div className="space-y-2">
                      <button
                        onClick={() => handleCopyPaymentLink(invoice.paymentLink)}
                        className="professional-btn professional-btn--secondary w-full"
                      >
                        <Copy className="w-4 h-4" />
                        Copy Payment Link
                      </button>
                    </div>
                  </div>
                ) : null
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="professional-modal-overlay" onClick={() => setSelectedInvoice(null)}>
          <div className="professional-modal professional-modal--large" onClick={(e) => e.stopPropagation()}>
            <div className="professional-modal__header">
              <h3 className="professional-modal__title">Invoice Details</h3>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="professional-modal__close"
              >
                ×
              </button>
            </div>
            <div className="professional-modal__content">
              <div className="space-y-6">
                <div className="professional-grid professional-grid--cols-2">
                  <div>
                    <h4 className="professional-heading-4 mb-2">Invoice Information</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Invoice Number:</strong> {selectedInvoice.invoiceNumber}</div>
                      <div><strong>Amount:</strong> {formatCurrency(selectedInvoice.amount, selectedInvoice.currency)}</div>
                      <div><strong>Due Date:</strong> {new Date(selectedInvoice.dueDate).toLocaleDateString()}</div>
                      <div><strong>Settlement Rail:</strong> {selectedInvoice.settlementRail}</div>
                      <div><strong>Status:</strong> 
                        <span className={`professional-status ${getStatusColor(selectedInvoice.status)} ml-2`}>
                          {getStatusIcon(selectedInvoice.status)}
                          <span className="professional-status__text">{selectedInvoice.status}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="professional-heading-4 mb-2">Customer Information</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Name:</strong> {selectedInvoice.customerName}</div>
                      <div><strong>Email:</strong> {selectedInvoice.customerEmail}</div>
                      <div><strong>Country:</strong> {selectedInvoice.customerCountry}</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="professional-heading-4 mb-2">Description</h4>
                  <p className="professional-text">{selectedInvoice.description}</p>
                </div>
                
                <div>
                  <h4 className="professional-heading-4 mb-2">Payment Link</h4>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={selectedInvoice.paymentLink}
                      readOnly
                      className="professional-form-input flex-1"
                    />
                    <button
                      onClick={() => handleCopyPaymentLink(selectedInvoice.paymentLink)}
                      className="professional-btn professional-btn--secondary"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}